/* eslint-disable max-lines */
import { Check } from 'lucide-react';
import * as React from 'react';

import { cn, toNfc } from '@heidi/utils';

import { Checkbox } from '../checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../command';
import { createBoundedScorer } from '../command/command.filters';
import { useDesignSystemIntl } from '../hooks/use-design-system-intl';
import { useIsExperimentalDesignSystemForms } from '../hooks/use-is-experimental-design-system-forms';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';
import { menuCheckboxRevealClasses } from '../styles/menu';
import { TypographyCaption, TypographyP2 } from '../typography';

import { ComboboxTrigger } from './combobox-trigger';
import {
  comboboxCommandClassesExperimental,
  comboboxErrorMessageClassesExperimental,
  comboboxItemVariantsExperimental,
} from './combobox.experimental.styles';
import {
  comboboxCheckIconClasses,
  comboboxCommandClasses,
  comboboxCommandInputClasses,
  comboboxContentVariants,
  comboboxErrorMessageClasses,
  comboboxItemLabelWithIconClasses,
  comboboxItemVariants,
  comboboxMultiCheckboxClasses,
  comboboxMultiItemBodyClasses,
  comboboxMultiItemDisabledClasses,
  comboboxRootClasses,
} from './combobox.styles';
import type { ComboboxItem, ComboboxProps } from './combobox.types';

const Combobox = React.forwardRef<HTMLDivElement, ComboboxProps>((props, ref) => {
  const {
    items,
    placeholder,
    searchPlaceholder,
    emptyText,
    disabled,
    buttonClassName,
    contentClassName,
    className,
    buttonProps,
    error,
    errorMessage,
    onOpenChange,
    leadingIcon,
    triggerRef,
    searchBy = 'value',
    'data-testid': testId,
  } = props;

  const [open, setOpen] = React.useState(false);
  const isExperimentalForms = useIsExperimentalDesignSystemForms();
  const intl = useDesignSystemIntl();

  const resolvedPlaceholder =
    placeholder ?? intl.formatMessage({ defaultMessage: 'Select', id: 'kQAf2d9u+x' });
  const resolvedSearchPlaceholder =
    searchPlaceholder ??
    intl.formatMessage({ defaultMessage: 'Search', id: 'xmcVZ0BU63' });
  const resolvedEmptyText =
    emptyText ??
    intl.formatMessage({
      defaultMessage: 'No results found',
      id: 'hX5PAbCTOR',
    });

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      setOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange]
  );

  const values = props.multiple ? props.values : null;
  const onValuesChange = props.multiple ? props.onValuesChange : undefined;
  const onDisabledItemSelect = props.multiple ? props.onDisabledItemSelect : undefined;
  const singleValue = props.multiple ? undefined : props.value;
  const onValueChange = props.multiple ? undefined : props.onValueChange;
  const clearable = props.multiple ? false : (props.clearable ?? true);
  const pinSelectedOnOpen = props.multiple ? (props.pinSelectedOnOpen ?? false) : false;

  const selectedItem = React.useMemo(
    () =>
      singleValue === undefined
        ? undefined
        : items.find((it) => it.value === singleValue),
    [items, singleValue]
  );
  const selectedMultiItems = React.useMemo(
    () => (values ? items.filter((it) => values.includes(it.value)) : []),
    [items, values]
  );

  // Snapshot a selected-first order on open and hold it while open, so toggling
  // doesn't reorder rows under the cursor. Set during render (not an effect) to
  // avoid an unpinned first frame; kept on close so the exit animation doesn't
  // reshuffle — it re-snapshots on the next open.
  const [pinnedOrder, setPinnedOrder] = React.useState<string[] | null>(null);
  const [pinTrackedOpen, setPinTrackedOpen] = React.useState(false);
  if (pinSelectedOnOpen && open && !pinTrackedOpen) {
    setPinTrackedOpen(true);
    const selectedAtOpen = new Set(values ?? []);
    setPinnedOrder(
      [
        ...items.filter((it) => selectedAtOpen.has(it.value)),
        ...items.filter((it) => !selectedAtOpen.has(it.value)),
      ].map((it) => it.value)
    );
  } else if (pinTrackedOpen && !open) {
    setPinTrackedOpen(false);
  }

  const displayItems = React.useMemo<ComboboxItem[]>(() => {
    if (!pinSelectedOnOpen || !pinnedOrder) return items;
    // Hold the frozen order but use the latest item objects (labels/disabled
    // update live); append rows that appeared and drop rows that left.
    const byValue = new Map(items.map((it) => [it.value, it]));
    const pinned = pinnedOrder
      .map((v) => byValue.get(v))
      .filter((it): it is ComboboxItem => it !== undefined);
    const pinnedValues = new Set(pinnedOrder);
    return [...pinned, ...items.filter((it) => !pinnedValues.has(it.value))];
  }, [items, pinnedOrder, pinSelectedOnOpen]);

  const isPlaceholder = props.multiple
    ? selectedMultiItems.length === 0
    : selectedItem === undefined;
  const triggerText = props.multiple
    ? selectedMultiItems.length > 0
      ? selectedMultiItems.map((it) => it.label).join(', ')
      : resolvedPlaceholder
    : selectedItem
      ? selectedItem.label
      : resolvedPlaceholder;

  const handleSelectSingle = React.useCallback(
    (currentValue: string) => {
      const willClear = clearable && currentValue === singleValue;
      const next = willClear ? null : currentValue;
      onValueChange?.(next ?? null);
      handleOpenChange(false);
    },
    [clearable, onValueChange, singleValue, handleOpenChange]
  );

  const handleSelectMulti = React.useCallback(
    (value: string, itemDisabled?: boolean) => {
      if (itemDisabled) {
        onDisabledItemSelect?.(value);
        return;
      }
      const current = values ?? [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      onValuesChange?.(next);
    },
    [onDisabledItemSelect, onValuesChange, values]
  );

  // Stays `undefined` off the opt-in path so Command's default NFC filter
  // scores the item value (not the label). searchBy="label" is the extra
  // work: score labels (and keywords) instead of values.
  const filter = React.useMemo(() => {
    if (searchBy !== 'label') {
      return undefined;
    }
    // Keyed on the trimmed value because that is what cmdk stores and hands
    // back — an untrimmed key would miss and silently score the value again.
    // NFC-normalize the label here (once per items/searchBy change) rather
    // than inside the returned closure: cmdk calls that closure once per
    // item per keystroke, so normalizing per-call would redo the same
    // label's normalization on every keystroke — O(items) wasted work each
    // time the user types, instead of once when the map is built.
    const labelsByValue = new Map(items.map((it) => [it.value.trim(), toNfc(it.label)]));
    const score = createBoundedScorer();
    // The query still normalizes at call time — cmdk's scorer does a
    // codepoint scan with no normalization, so a precomposed label (NFC)
    // against a decomposed IME query (NFD, e.g. macOS Korean input) scores 0
    // despite rendering identically.
    return (itemValue: string, search: string, keywords?: string[]) =>
      score(
        labelsByValue.get(itemValue) ?? toNfc(itemValue),
        toNfc(search),
        keywords?.map(toNfc)
      );
  }, [searchBy, items]);

  const listId = React.useId();
  const errorId = React.useId();

  return (
    <div ref={ref} data-testid={testId} className={cn(comboboxRootClasses, className)}>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <ComboboxTrigger
            ref={triggerRef}
            open={open}
            disabled={disabled}
            error={error}
            isPlaceholder={isPlaceholder}
            leadingIcon={leadingIcon}
            className={buttonClassName}
            aria-controls={listId}
            aria-describedby={errorMessage ? errorId : undefined}
            {...buttonProps}
          >
            {triggerText}
          </ComboboxTrigger>
        </PopoverTrigger>
        <PopoverContent
          matchTriggerWidth
          data-experimental={isExperimentalForms || undefined}
          className={cn(comboboxContentVariants(), contentClassName)}
        >
          <Command
            filter={filter}
            className={
              isExperimentalForms
                ? comboboxCommandClassesExperimental
                : comboboxCommandClasses
            }
          >
            <CommandInput
              // eslint-disable-next-line jsx-a11y/no-autofocus -- overlay focus management: moves focus into the just-opened popover, not page-load autofocus
              autoFocus
              placeholder={resolvedSearchPlaceholder}
              className={comboboxCommandInputClasses}
            />
            <CommandList
              id={listId}
              className={props.multiple ? menuCheckboxRevealClasses : undefined}
            >
              <CommandEmpty>{resolvedEmptyText}</CommandEmpty>
              <CommandGroup>
                {displayItems.map((item) => {
                  const itemClassName = isExperimentalForms
                    ? comboboxItemVariantsExperimental()
                    : comboboxItemVariants();

                  if (props.multiple) {
                    const isSelected = values?.includes(item.value) ?? false;
                    const softDisabled = Boolean(item.disabled);
                    // A soft-disabled row isn't cmdk-`disabled` (that would swallow
                    // its click), so its state rides the recipe/checkbox. The closure
                    // carries `item.disabled`, which cmdk's `onSelect(value)` omits.
                    return (
                      <CommandItem
                        key={item.value}
                        value={item.value}
                        keywords={item.keywords}
                        data-testid={item.testId}
                        onSelect={() => handleSelectMulti(item.value, item.disabled)}
                        // items-start so the checkbox tracks the title, not the
                        // centre of a row a description makes tall.
                        className={cn(
                          itemClassName,
                          'items-start',
                          softDisabled && comboboxMultiItemDisabledClasses
                        )}
                      >
                        <Checkbox
                          checked={isSelected}
                          disabled={softDisabled}
                          onCheckedChange={() => {}}
                          // Decorative: cmdk's role="option" makes descendants
                          // presentational, so the box conveys nothing to AT — it's
                          // aria-hidden and non-focusable, mirroring multi-select-filter.
                          // me-2 only in stable; the experimental CommandItem supplies gap-2.
                          aria-hidden="true"
                          className={cn(
                            comboboxMultiCheckboxClasses,
                            !isExperimentalForms && 'me-2'
                          )}
                          tabIndex={-1}
                        />
                        {item.icon}
                        <div className={comboboxMultiItemBodyClasses}>
                          <TypographyP2 className="truncate leading-5">
                            {item.label}
                          </TypographyP2>
                          {item.description != null && (
                            <TypographyCaption className="text-text-secondary">
                              {item.description}
                            </TypographyCaption>
                          )}
                        </div>
                      </CommandItem>
                    );
                  }

                  return (
                    <CommandItem
                      key={item.value}
                      value={item.value}
                      keywords={item.keywords}
                      data-testid={item.testId}
                      onSelect={handleSelectSingle}
                      className={itemClassName}
                    >
                      {item.icon}
                      <TypographyP2
                        className={cn(
                          'leading-5',
                          // Experimental CommandItem supplies gap-2; stable still
                          // needs the label margin when an item has an icon.
                          item.icon &&
                            !isExperimentalForms &&
                            comboboxItemLabelWithIconClasses
                        )}
                      >
                        {item.label}
                      </TypographyP2>
                      <Check
                        className={cn(
                          comboboxCheckIconClasses,
                          singleValue === item.value ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {errorMessage &&
        (isExperimentalForms ? (
          <TypographyP2 id={errorId} className={comboboxErrorMessageClassesExperimental}>
            {errorMessage}
          </TypographyP2>
        ) : (
          <TypographyCaption id={errorId} className={comboboxErrorMessageClasses}>
            {errorMessage}
          </TypographyCaption>
        ))}
    </div>
  );
});
Combobox.displayName = 'Combobox';

export { Combobox };
